import { Component, Input, OnInit, ElementRef, AfterViewInit, HostListener } from '@angular/core';
import { DashboardGraph } from 'src/app/model/dashboard';
import { AdminService } from 'src/app/service/admin.service';
import { TranslateService } from '@ngx-translate/core';
import { SystemService } from 'src/app/service/system.service';
import { ColorPalettes, PaletteName } from 'src/app/utils/color-palettes';
import { SubmissionStatus, SubmissionStatusStyles } from 'src/app/enum/submission.status';
import { ReviewStatus } from 'src/app/enum/review.status';
import html2canvas from 'html2canvas';
import * as domToImage from 'dom-to-image';

type DatasetLabel = string | number | Date;
type DatasetSeries = {
  labels?: DatasetLabel[];
  series: any; // Can be number[] | Array<number[]> | Array<{ value: number, className?: string }>
};

@Component({
  selector: 'app-dashboard-chart-v2',
  templateUrl: './dashboard-chart-v2.component.html',
  styleUrls: ['./dashboard-chart-v2.component.scss']
})
export class DashboardChartV2Component implements OnInit, AfterViewInit {
  @Input() graphPosition: DashboardGraph;
  @Input() palette: PaletteName = 'adwaita';

  // Inputs for ngx-charts
  chartKind: 'Pie' | 'Bar' | 'Line' | 'Gauge' | 'Unknown' = 'Unknown';
  data: any[] = [];
  view: [number, number] = [500, 300];

  // Color palette setup via utility (Adwaita default, Tango optional)
  colorScheme = { domain: ColorPalettes.getDomain(this.palette) };



  xAxisLabel = '';
  yAxisLabel = '';
  legend = true;
  legendPosition: 'right' | 'below' = 'right';
  hasData = false;
  // Preferred label for series legend/tooltip
  private seriesLabel = 'Series';
  // Current dataset name for color scheme selection
  private currentDatasetName = '';

  constructor(
    private adminService: AdminService,
    private translateService: TranslateService,
    private systemService: SystemService,
    private elRef: ElementRef
  ) { }

  ngOnInit(): void {
    if (!this.graphPosition || !this.graphPosition.dataset) return;
    const datasetName = this.graphPosition.dataset['datasetName'];
    const datasetType = this.graphPosition.dataset['type'];

    this.currentDatasetName = datasetName;
    this.seriesLabel = this.resolveSeriesLabel(datasetName);

    const validChartKinds = ['Pie', 'Bar', 'Line'] as const;
    this.chartKind = validChartKinds.includes(datasetType) ? datasetType : 'Unknown';

    // Fetch data in the same way the old component does
    this.adminService.getDatasetData(datasetName).subscribe(raw => {
      const d: DatasetSeries = raw[datasetName];
      if (!d || !d.labels) { this.hasData = false; return; }

      // Convert every label to string for downstream processing
      let stringLabels: string[] = d.labels.map(l => String(l));

      // Basic label normalization
      stringLabels = stringLabels.map(l => {
        if (l === 'undefined') return this.translateService.instant('dataset-options.undefinedLabel');
        if (l === 'Others') return this.translateService.instant('dataset-options.othersLabel');
        if (l === 'no_affiliation') return this.translateService.instant('dataset-options.no_affiliationLabel');
        if (l === '') return this.translateService.instant('dataset-options.unknownCountryLabel');
        return l;
      });
      d.labels = stringLabels;

      // Map country ISO to names if applicable
      if (['submissionsByCountry', 'authorsByCountry'].includes(datasetName)) {
        this.systemService.countries.subscribe(countries => {
          d.labels = (d.labels as string[]).map((l: string) => {
            const c = countries.find(cc => cc.isoCode === l);
            return c ? c.name : l;
          });
          this.translateDataset(d);
          this.mapToNgx(d);
        });
        return; // mapping continues inside subscription
      }

      // Stable order for status-based datasets
      if (datasetName === 'submissionsByStatus') {
        const desired = SubmissionStatus.toIterable();
        const indexMap = (d.labels as string[]).reduce((acc, label, idx) => { acc[label] = idx; return acc; }, {} as Record<string, number>);
        const newOrder = desired.filter(lbl => lbl in indexMap);
        const newSeries = newOrder.map(lbl => (Array.isArray(d.series) ? d.series[indexMap[lbl]] : 0));
        d.labels = newOrder;
        d.series = newSeries;
      }

      if (datasetName === 'reviewsByStatus') {
        const desired = ReviewStatus.toIterable();
        const indexMap = (d.labels as string[]).reduce((acc, label, idx) => { acc[label] = idx; return acc; }, {} as Record<string, number>);
        const newOrder = desired.filter(lbl => lbl in indexMap);
        const newSeries = newOrder.map(lbl => (Array.isArray(d.series) ? d.series[indexMap[lbl]] : 0));
        d.labels = newOrder;
        d.series = newSeries;
      }

      this.translateDataset(d);
      this.mapToNgx(d);
    });
  }

  ngAfterViewInit(): void {
    this.updateChartWidth();
  }

  @HostListener('window:resize')
  onResize() {
    this.updateChartWidth();
  }

  private updateChartWidth() {
    try {
      const host: HTMLElement = this.elRef.nativeElement;
      // Find the closest column container to get available width
      const column = host.closest('.column') as HTMLElement || host.parentElement as HTMLElement;
      // Reserve extra space: 32px base padding + ~40px for menu button + ~20px for y-axis label
      const EDGE_PADDING = 92;
      const width = Math.max(380, Math.floor((column?.clientWidth || host.clientWidth) - EDGE_PADDING));
      // Standardize vertical card size by using a constant height for all charts
      const CONSTANT_HEIGHT = 380;
      this.view = [width, CONSTANT_HEIGHT];
      // Always use horizontal legend below with wrapping to avoid truncation on large monitors
      this.legendPosition = 'below';
    } catch (_) { }
  }

  private translateDataset(d: DatasetSeries) {
    let translateLabelPrepend = '';
    let translateLabelFlag = false;

    // Check if the backend instructed us to translate this dataset
    if (this.graphPosition && this.graphPosition.dataset && Array.isArray(this.graphPosition.dataset['options'])) {
      const customOpt = this.graphPosition.dataset['options'].find((o: any) => o.name === 'customOption');
      if (customOpt && customOpt.settings && customOpt.settings.translateLabel) {
        translateLabelFlag = true;
        translateLabelPrepend = customOpt.settings.translateLabelPrepend || '';
      }
    }

    const translateLabel = (label: string | number | Date): string => {
      const strLabel = String(label ?? '').trim();

      if (translateLabelFlag) {
        const key = `${translateLabelPrepend}${strLabel}`;
        const translated = this.translateService.instant(key);
        if (translated !== key) return translated;
      }

      if (strLabel.toLowerCase() === 'total') {
        return this.translateService.instant('admin.event.statistics.tables.topics-submission.total');
      }

      return strLabel;
    };

    if (d.labels) {
      d.labels = d.labels.map(l => translateLabel(l));
    }
    if ((d as any).seriesNames && Array.isArray((d as any).seriesNames)) {
      (d as any).seriesNames = ((d as any).seriesNames as string[]).map(l => translateLabel(l));
    }
  }

  is2DBar = false;

  private mapToNgx(d: DatasetSeries) {
    // Normalize and map to ngx-charts format
    if (this.chartKind === 'Pie' || this.chartKind === 'Gauge') {
      const series = Array.isArray(d.series) ? d.series : [];
      this.data = d.labels.map((label, idx) => {
        const v = series[idx];
        const value = typeof v === 'object' && v !== null && 'value' in v ? v.value : v;
        const numericValue = Number(value || 0);
        return { name: `${label} (${numericValue})`, value: numericValue };
      });
      this.hasData = this.data.some(x => x.value > 0);
      this.applyPaletteDomain(this.data.length, this.currentDatasetName);
    } else if (this.chartKind === 'Bar' || this.chartKind === 'Line') {
      const s = d.series;
      const isLine = this.chartKind === 'Line';
      const seriesNames = Array.isArray((d as any).seriesNames) ? (d as any).seriesNames : undefined;
      const baseLabel = this.seriesLabel || 'Series';
      const isMultiSeries = Array.isArray(s) && s.length > 1 && Array.isArray(s[0]);

      // Determine if it should be a 2D Bar Chart. Line charts are naturally 2D
      // 1D Bar Charts ONLY for numerical cases
      // For text-based cases, uses 2D grouped bars so long labels go to the legend and don't crowd the x-axis
      const allLabelsAreNumeric = d.labels && d.labels.length > 0 && d.labels.every(l => !isNaN(Number(String(l).trim())));
      this.is2DBar = isLine || isMultiSeries || (this.chartKind === 'Bar' && !allLabelsAreNumeric);

      if (this.chartKind === 'Bar' && !this.is2DBar) {
        // 1D Bar Chart mapping
        const flatSeries = Array.isArray(s) ? s.flat() : [];
        this.data = d.labels.map((label, idx) => {
          const valueSource = flatSeries[idx];
          const numericValue = typeof valueSource === 'object' && valueSource !== null && 'value' in valueSource ? valueSource.value : valueSource;
          return { name: String(label ?? '').trim(), value: Number(numericValue || 0) };
        });
      } else {
        // 2D Line/Bar Chart mapping
        if (isMultiSeries) {
          this.data = s.map((serie: number[], sIdx: number) => ({
            name: seriesNames?.[sIdx] || `${baseLabel} ${sIdx + 1}`,
            series: d.labels.map((label, idx) => ({ name: isLine ? this.toDateIfLine(label) : String(label ?? '').trim(), value: Number(serie[idx] || 0) }))
          }));
        } else if (Array.isArray(s)) {
          const flatSeries = s.flat();
          const points = d.labels.map((label, idx) => {
            const valueSource = flatSeries[idx];
            const numericValue = typeof valueSource === 'object' && valueSource !== null && 'value' in valueSource ? valueSource.value : valueSource;
            const normalizedLabel = isLine ? this.toDateIfLine(label) : String(label ?? '').trim();
            return { name: normalizedLabel, value: Number(numericValue || 0) };
          });
          const singleName = seriesNames?.[0] || baseLabel;
          this.data = [{ name: singleName, series: points }];
        } else {
          this.data = [];
        }
      }

      // Check if there's any value greater than 0
      this.hasData = this.is2DBar
        ? this.data.some(group => group.series?.some(item => item.value > 0))
        : this.data.some(item => item.value > 0);
      this.resolveAxisLabels(this.currentDatasetName);
      // Handle single-group/series specifics per chart type
      if (this.data.length === 1) {
        if (this.chartKind === 'Bar') {
          // For single-group bar charts, the group name renders as an x-axis label
          // inside the chart. Replace it with the proper xAxisLabel (from ctAxisTitle)
          // and disable [showXAxisLabel] to avoid duplication.
          if (this.xAxisLabel) {
            this.data[0].name = this.xAxisLabel;
            this.xAxisLabel = '';
          }
        } else if (this.chartKind === 'Line') {
          // Single-series line charts don't need a legend (one colored line is self-evident)
          this.legend = false;
        }
      }
      // For line charts, ensure chronological order and aggregate duplicate dates
      if (isLine) {
        this.data = this.data.map(group => {
          const series = Array.isArray(group.series) ? group.series.slice() : [];
          const allDates = series.every(p => p.name instanceof Date);
          if (allDates) {
            // Aggregate duplicates by timestamp
            const byTs = new Map<number, number>();
            for (const p of series) {
              const ts = (p.name as Date).getTime();
              byTs.set(ts, (byTs.get(ts) || 0) + Number(p.value || 0));
            }
            const dedup = Array.from(byTs.entries()).map(([ts, val]) => ({ name: new Date(ts), value: val }));
            dedup.sort((a, b) => (a.name as Date).getTime() - (b.name as Date).getTime());
            return { ...group, series: dedup };
          }
          return group;
        });
      }
      // Use unique item names as category count for color domain
      const names = new Set<string>();
      if (!isLine) {
        this.data.forEach(group => group.series?.forEach((item: any) => names.add(String(item.name))));
        this.applyPaletteDomain(names.size || undefined, this.currentDatasetName);
      } else {
        // For line charts, color domain should reflect number of series (lines)
        this.applyPaletteDomain(this.data.length || undefined, this.currentDatasetName);
      }
      // Recalculate chart dimensions now that axis labels are resolved
      this.updateChartWidth();
    } else {
      this.hasData = false;
    }
  }

  private applyPaletteDomain(count?: number, datasetName?: string) {
    // Special color schemes for specific datasets
    if (datasetName === 'submissionsByStatus') {
      // Use submission status colors: Active-yellow, Pending-orange, Accepted-green, Rejected-red, Withdrawn-gray
      const statusOrder = SubmissionStatus.toIterable();
      const domain = statusOrder.map(status => SubmissionStatusStyles[status]?.color || '#888888');
      this.colorScheme = { domain };
      return;
    }

    if (datasetName === 'submissionsByReviewsCompleted') {
      // Gradient from red (no reviews) to green (more reviews)
      const domain = this.generateRedToGreenGradient(count || 5);
      this.colorScheme = { domain };
      return;
    }

    // Default: use palette colors
    const domain = ColorPalettes.getDomain(this.palette, count);
    this.colorScheme = { domain };
  }

  // Generate a gradient from red to green for reviews completed chart
  private generateRedToGreenGradient(steps: number): string[] {
    const colors: string[] = [];
    for (let i = 0; i < steps; i++) {
      const ratio = steps === 1 ? 1 : i / (steps - 1);
      // Interpolate from red (#e53935) to green (#43a047)
      const r = Math.round(229 - ratio * (229 - 67));
      const g = Math.round(57 + ratio * (160 - 57));
      const b = Math.round(53 + ratio * (71 - 53));
      colors.push(`rgb(${r}, ${g}, ${b})`);
    }
    return colors;
  }

  private resolveSeriesLabel(datasetName: string): string {
    const legendKey = `dataset-options.legends.${datasetName}`;
    const translatedLegend = this.translateService.instant(legendKey);
    if (translatedLegend && translatedLegend !== legendKey) return translatedLegend;

    const titleKey = this.graphPosition?.title;
    if (titleKey) {
      const translatedTitle = this.translateService.instant(titleKey);
      if (translatedTitle && translatedTitle !== titleKey) return translatedTitle;
    }

    return datasetName || 'Series';
  }

  private resolveAxisLabels(datasetName: string): void {
    // Read axis labels from the dataset options provided by the backend (ctAxisTitle plugin settings)
    const dataset = this.graphPosition.dataset as any;
    if (!dataset?.options) {
      this.xAxisLabel = '';
      this.yAxisLabel = '';
      return;
    }

    const axisTitleOpt = dataset.options.find((opt: any) => opt.name === 'ctAxisTitle');
    if (axisTitleOpt?.settings) {
      const xKey = axisTitleOpt.settings?.axisX?.axisTitle || '';
      const yKey = axisTitleOpt.settings?.axisY?.axisTitle || '';
      this.xAxisLabel = xKey ? this.translateService.instant(xKey) : '';
      this.yAxisLabel = yKey ? this.translateService.instant(yKey) : '';
    } else {
      this.xAxisLabel = '';
      this.yAxisLabel = '';
    }
  }

  // If current chart is Line, attempt to convert label to Date for ngx-charts time scale.
  private toDateIfLine(label: DatasetLabel): any {
    if (this.chartKind !== 'Line') return String(label ?? '').trim();
    // Already a Date
    if (label instanceof Date) return label;
    // Numeric timestamp (seconds or ms)
    if (typeof label === 'number') {
      const ts = label > 1e12 ? label : (label > 1e10 ? label * 1000 : label * 1000);
      const d = new Date(ts);
      return isNaN(d.getTime()) ? String(label) : d;
    }
    const str = String(label ?? '').trim();
    const parsed = new Date(str);
    // Rely on native/ISO parse; otherwise keep as string so axis stays categorical
    return isNaN(parsed.getTime()) ? str : parsed;
  }

  saveAsImage(type: string): void {
    // Capture the chart container element
    const chartContainer = this.elRef.nativeElement.querySelector('.chart-container-v2') as HTMLElement;
    if (chartContainer) {
      const filename = this.graphPosition?.title
        ? this.translateService.instant(this.graphPosition.title).replace(/\s+/g, '_').toLowerCase()
        : 'chart';

      // Function to filter out the menu-wrapper so it doesn't appear in the downloaded image
      const filterMenu = (node: Element) => {
        return !node.classList || !node.classList.contains('menu-wrapper');
      };

      switch (type) {
        case 'png':
          html2canvas(chartContainer, {
            logging: false,
            ignoreElements: (element: Element) => element.classList && element.classList.contains('menu-wrapper')
          }).then((canvas) => {
            const link = document.createElement('a');
            link.href = canvas.toDataURL('image/png');
            link.download = `${filename}.png`;
            link.click();
          }).catch(error => {
            console.error('Error capturing the chart:', error);
          });
          break;
        case 'svg':
          domToImage.toSvg(chartContainer, {
            logging: false,
            filter: filterMenu as any
          }).then((dataUrl: string) => {
            const link = document.createElement('a');
            link.href = dataUrl;
            link.download = `${filename}.svg`;
            link.click();
          }).catch((error: any) => {
            console.error('Error capturing the chart:', error);
          });
          break;
      }
    }
  }
}
